import React, { useState, useEffect, useCallback, useRef } from 'react';
import './CardList.css';
import Popup from './Popup';
import SkuDetailsPopup from './SkuDetailsPopup';
import PriceDebugPopup from './PriceDebugPopup';
import PriceInquiryForm from './PriceInquiryForm';
import ShareButtons from './ShareButtons';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import i18n from '../i18n';
import { resolveUniqueUnopenedSku, sealedSelection } from '../utils/sealedCatalogIdentity';
import { getOrbitSelection, orbitVariantLabel } from '../orbit/orbitVariant';

// Preferred condition order: NM first if allowed, then LP, MP, HP, DMG
const EMPTY_CART = [];
const CONDITION_ORDER = ['NM', 'LP', 'MP', 'HP', 'DMG'];

// Games where Japanese is the native/base language (treated like EN for other games)
const JAPANESE_NATIVE_GAMES = ['Pokemon Japan'];

// Helper function to get the native/base language for a game
const getNativeLanguage = (gameName) => {
  if (JAPANESE_NATIVE_GAMES.includes(gameName)) {
    return 'JP';
  }
  return 'EN';
};

// Helper function to get the best default condition based on allowed conditions
const getDefaultCondition = (cardConditions, allowedConditions, preferredCondition) => {
  if (preferredCondition && allowedConditions.includes(preferredCondition) && cardConditions.includes(preferredCondition)) return preferredCondition;
  // If NM is in allowed conditions and card has NM, use NM
  if (allowedConditions.includes('NM') && cardConditions.includes('NM')) {
    return 'NM';
  }
  // Otherwise, find the first condition in preferred order that's both allowed and available
  for (const cond of CONDITION_ORDER) {
    if (allowedConditions.includes(cond) && cardConditions.includes(cond)) {
      return cond;
    }
  }
  // Fallback to first available condition
  return cardConditions[0] || '';
};

// Helper function to sort conditions in preferred order
const sortConditions = (conditions) => {
  return [...conditions].sort((a, b) => {
    const indexA = CONDITION_ORDER.indexOf(a);
    const indexB = CONDITION_ORDER.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
};

const selectableSkus = (card) => {
  const resolution = resolveUniqueUnopenedSku(card);
  if (!resolution.sealed) return Array.isArray(card?.skus) ? card.skus : [];
  return resolution.error ? [] : [resolution.sku];
};

const selectableConditions = (card, allowedConditions) => {
  const sealed = sealedSelection(card);
  if (sealed.sealed) return sealed.error ? [] : [sealed.condition];
  return sortConditions([...new Set(selectableSkus(card)
    .map((sku) => sku.condAbbr)
    .filter((condition) => allowedConditions.includes(condition)))]);
};

const CardList = ({ cards, addToCart, allowedConditions, floorPrice, bulkSettings, userSettings, excludedSku, calculatePrice, exchangeRates, isCashLimitReached, hotlistItems = [], hotlistCashPercent = 70, hotlistCreditPercent = 80, hotlistFixedPrices = {}, showSearchModeToggle = false, currentSearchMode = "single", onSearchModeToggle = () => {}, itemsPerPage = 25, setItemsPerPage = () => {}, cartItems = EMPTY_CART, simplistic = false, orbit = false, viewMode, defaultCondition, paymentType = 'cash' }) => {
  const { t } = useTranslation();
  const [selectedConditions, setSelectedConditions] = useState({});
  const [selectedPrintings, setSelectedPrintings] = useState({});
  const [selectedLanguages, setSelectedLanguages] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [showSkuDetailsPopup, setShowSkuDetailsPopup] = useState(false);
  const [showPriceDebugPopup, setShowPriceDebugPopup] = useState(false);
  const [showPriceInquiryForm, setShowPriceInquiryForm] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [prices, setPrices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const isDKeyPressed = useRef(false);
  // Check if this is SmokeandMirrors user or v3 template enabled
  const isSmokeandMirrors = userSettings?.username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true;
  // Check if this is MinMaxGames user (shows Not Buying toggle even in v3 template)
  const isMinMaxGames = userSettings?.username?.toLowerCase() === 'minmaxgames';

  const [showBulkItems, setShowBulkItems] = useState(() => {
    const saved = localStorage.getItem('showBulkItems');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showNotBuying, setShowNotBuying] = useState(() => {
    // For SmokeandMirrors or v3 template (except MinMaxGames), default to false (hide not buying items)
    const isMinMax = userSettings?.username?.toLowerCase() === 'minmaxgames';
    if ((userSettings?.username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true) && !isMinMax) {
      return false;
    }
    const saved = localStorage.getItem('showNotBuying');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [localTableView, setIsTableView] = useState(() => {
    const saved = localStorage.getItem('isTableView');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const isTableView = viewMode ? viewMode === 'table' : localTableView;
  useEffect(() => { if (simplistic) setSelectedConditions({}); }, [simplistic, defaultCondition]);
  const [showTooltips, setShowTooltips] = useState(() => {
    const saved = localStorage.getItem('showTooltips');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  // Helper to count matching cart items for inventory-aware pricing
  // Items match when they share: productId + printing + language
  // Note: Different conditions (NM, LP, MP, HP) count TOGETHER toward the threshold
  const countMatchingCartItems = useCallback((productId, printing, language) => {
    if (!cartItems || cartItems.length === 0) return 0;
    return cartItems.filter(item =>
      item.productId === productId &&
      item.printing === printing &&
      item.language === language
    ).length;
  }, [cartItems]);

  const handleLanguageChange = useCallback((cardId, newLanguage) => {
    setSelectedLanguages(prev => ({ ...prev, [cardId]: newLanguage }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadQuotes = async () => {
      setIsLoading(true);
      const nextPrices = {};
      for (let start = 0; start < cards.length; start += 5) {
        await Promise.all(cards.slice(start, start + 5).map(async card => {
          const sealed = sealedSelection(card);
          const orbitChoice = orbit ? getOrbitSelection(card, allowedConditions, { condition: selectedConditions[card._id], printing: selectedPrintings[card._id], language: selectedLanguages[card._id] }, userSettings) : null;
          const skus = selectableSkus(card);
          const cardConditions = selectableConditions(card, allowedConditions);
          const nativeLanguage = sealed.sealed ? sealed.language : getNativeLanguage(card.gameName);
          const condition = orbit ? orbitChoice.condition : sealed.sealed ? sealed.condition : selectedConditions[card._id] || getDefaultCondition(cardConditions, allowedConditions, defaultCondition);
          const printing = orbit ? orbitChoice.printing : sealed.sealed ? sealed.printing : selectedPrintings[card._id] || skus[0]?.printingName || '';
          if ((sealed.sealed && sealed.error) || (orbit && !orbitChoice.sku)) {
            nextPrices[card._id] = { currentPrice: { cashPrice: 0, creditPrice: 0, contactForPrice: false, catalogIdentityError: sealed.error || 'No eligible variant' }, priceMap: {}, availableLanguages: [] };
            return;
          }
          const languages = new Set(orbit ? [orbitChoice.language] : skus.map(sku => sku.langAbbr));
          if (!orbit && !sealed.sealed && typeof userSettings?.langPercent === 'object') {
            Object.keys(userSettings.langPercent).forEach(language => languages.add(language));
          } else if (!orbit && !sealed.sealed && userSettings?.langPercent != null) {
            ['EN', 'JP', 'DE', 'FR', 'ES', 'IT', 'PT', 'RU', 'KO', 'ZH', 'PL'].forEach(language => languages.add(language));
          }
          const defaultLanguage = languages.has(nativeLanguage) ? nativeLanguage : [...languages][0] || nativeLanguage;
          const language = orbit ? orbitChoice.language : sealed.sealed ? sealed.language : selectedLanguages[card._id] || defaultLanguage;
          languages.add(language);
          const priceMap = {};
          await Promise.all([...languages].map(async code => {
            const offset = countMatchingCartItems(card.productId, printing, code) + 1;
            priceMap[code] = await calculatePrice(card, condition, printing, code, true, false, offset);
          }));
          nextPrices[card._id] = { currentPrice: priceMap[language], priceMap, availableLanguages: [...languages] };
        }));
      }
      if (!cancelled) { setPrices(nextPrices); setIsLoading(false); }
    };
    loadQuotes().catch(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [cards, selectedConditions, selectedPrintings, selectedLanguages, allowedConditions, calculatePrice, userSettings, countMatchingCartItems, defaultCondition, orbit]);

  const handleConditionChange = useCallback((cardId, newCondition) => {
    setSelectedConditions(prev => ({ ...prev, [cardId]: newCondition }));
  }, []);

  const handlePrintingChange = useCallback((cardId, newPrinting) => {
    setSelectedPrintings(prev => ({ ...prev, [cardId]: newPrinting }));
  }, []);

  const handleQuantityChange = useCallback((cardId, newQuantity) => {
    const qty = Math.max(1, Math.min(simplistic || orbit ? 4 : 999, parseInt(newQuantity) || 1));
    setSelectedQuantities(prev => ({ ...prev, [cardId]: qty }));
  }, [simplistic, orbit]);

  const handleAddToCart = useCallback(async (card, isCash) => {
    const orbitChoice = orbit ? getOrbitSelection(card, allowedConditions, { condition: selectedConditions[card._id], printing: selectedPrintings[card._id], language: selectedLanguages[card._id] }, userSettings) : null;
    const sealed = sealedSelection(card);
    if (sealed.sealed && sealed.error) {
      window.alert(`${sealed.error} Nothing was added.`);
      return;
    }
    const cardConditions = selectableConditions(card, allowedConditions);
    const condition = orbit ? orbitChoice.condition : sealed.sealed
      ? sealed.condition
      : selectedConditions[card._id] || getDefaultCondition(cardConditions, allowedConditions, defaultCondition);
    const printing = orbit ? orbitChoice.printing : sealed.sealed
      ? sealed.printing
      : selectedPrintings[card._id] || selectableSkus(card)[0]?.printingName || '';
    const language = orbit ? orbitChoice.language : sealed.sealed ? sealed.language : selectedLanguages[card._id] || (selectableSkus(card).some(sku => sku.langAbbr === getNativeLanguage(card.gameName)) ? getNativeLanguage(card.gameName) : selectableSkus(card)[0]?.langAbbr || 'EN');
    const quantity = selectedQuantities[card._id] || 1;
    
    // Get the price data for the selected language
    const cardPriceData = prices[card._id];
    if (!cardPriceData || !cardPriceData.currentPrice) {
      console.error('Price not found for card:', card._id);
      return;
    }

    const currentPrice = cardPriceData.currentPrice;
    const basePrice = currentPrice.basePrice;
    const offeredPrice = isCash ? currentPrice.cashPrice : currentPrice.creditPrice;
    
    // For non-English cards, we need to find the equivalent EN SKU
    // but add a note that it's actually a non-English card
    let sku;
    let orderNote = '';
    
    if (orbit) {
      sku = orbitChoice.sku;
      if (language !== 'EN') orderNote = orbitChoice.derivedLanguage && currentPrice.isDerivedPrice
        ? `This ${card.name} is actually a ${language} language card (${currentPrice.derivedPercentage}% of EN price).`
        : `This ${card.name} is a ${language} language card.`;
    } else if (sealed.sealed) {
      sku = sealed.sku;
    } else if (language !== 'EN') {
      // First try to find the exact SKU with the selected language
      const exactSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
      
      // Then find the equivalent EN SKU
      const enSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === 'EN'
      );
      
    // If we have an EN SKU, use it but add a note
      if (enSku) {
        sku = enSku;
        // Include the percentage in the note if it's a derived price
        if (currentPrice.isDerivedPrice) {
          orderNote = `This ${card.name} is actually a ${language} language card (${currentPrice.derivedPercentage}% of EN price).`;
        } else {
          orderNote = `This ${card.name} is actually a ${language} language card.`;
        }
      } else {
        // If no EN SKU exists, use the exact language SKU if available
        sku = exactSku;
      }
    } else {
      // For English cards, just find the matching SKU
      sku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
    }

    console.log('Adding to cart:', {
      name: card.name,
      condition,
      printing,
      language,
      basePrice,
      offeredPrice,
      isCash,
      skuId: sku?.skuId,
      isDerivedPrice: currentPrice.isDerivedPrice,
      derivedPercentage: currentPrice.derivedPercentage,
      orderNote
    });

    // Create a nonEnglish field for non-English cards
    let nonEnglish = null;
    if (language !== 'EN') {
      nonEnglish = {
        language,
        offeredPrice,
        isDerivedPrice: currentPrice.isDerivedPrice,
        derivedPercentage: currentPrice.derivedPercentage,
        basePrice
      };
    }

    // Pass the actual selected language, order note, and nonEnglish data to addToCart
    await addToCart(card, condition, printing, quantity, sku?.skuId, isCash, language, orderNote, nonEnglish);
  }, [selectedConditions, selectedPrintings, selectedLanguages, selectedQuantities, addToCart, prices, allowedConditions, defaultCondition, orbit, userSettings]);

  const handleContactForPrice = useCallback((card) => {
    setSelectedCard(card);
    setShowPriceInquiryForm(true);
  }, []);

  const closePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  const handleOpenSkuDetails = useCallback((card) => {
    if (isDKeyPressed.current) {
      setSelectedCard(card);
      setShowSkuDetailsPopup(true);
    }
  }, []);

  // Handler for D + Click to open Price Debug Popup
  const handleOpenPriceDebug = useCallback((card) => {
    if (isDKeyPressed.current) {
      setSelectedCard(card);
      setShowPriceDebugPopup(true);
      return true; // Indicate that debug popup was opened
    }
    return false; // Indicate normal click should proceed
  }, []);

  const closeSkuDetailsPopup = useCallback(() => {
    setShowSkuDetailsPopup(false);
    setSelectedCard(null);
  }, []);

  const closePriceDebugPopup = useCallback(() => {
    setShowPriceDebugPopup(false);
    setSelectedCard(null);
  }, []);

  const closePriceInquiryForm = useCallback(() => {
    setShowPriceInquiryForm(false);
    setSelectedCard(null);
  }, []);


  const handleImageClick = useCallback((imageUrl, cardName) => {
    setEnlargedImage({ url: imageUrl, name: cardName });
  }, []);

  const closeEnlargedImage = useCallback(() => {
    setEnlargedImage(null);
  }, []);

  // Tooltip content for different options
  const getConditionTooltip = (condition) => {
    const tooltips = {
      'NM': 'Near Mint - Like new condition with minimal wear',
      'LP': 'Lightly Played - Minor wear visible on close inspection',
      'MP': 'Moderately Played - Moderate wear, still tournament legal',
      'HP': 'Heavily Played - Significant wear but still recognizable',
      'DMG': 'Damaged - Major wear, creases, or other damage'
    };
    return tooltips[condition] || 'Card condition affects value and playability';
  };

  const getPrintingTooltip = (printing) => {
    if (printing.toLowerCase().includes('foil')) {
      return 'Foil - Special shiny/holographic version of the card';
    }
    if (printing.toLowerCase().includes('first') || printing.toLowerCase().includes('1st')) {
      return 'First Edition - Original printing run, often more valuable';
    }
    if (printing.toLowerCase().includes('unlimited')) {
      return 'Unlimited - Standard printing without edition marking';
    }
    return 'Printing type - Different versions or editions of the same card';
  };

  const getLanguageTooltip = (language) => {
    const tooltips = {
      'EN': 'English - Standard English language version',
      'JP': 'Japanese - Original Japanese language version',
      'DE': 'German - German language version',
      'FR': 'French - French language version',
      'ES': 'Spanish - Spanish language version',
      'IT': 'Italian - Italian language version',
      'PT': 'Portuguese - Portuguese language version',
      'RU': 'Russian - Russian language version',
      'KO': 'Korean - Korean language version',
      'ZH': 'Chinese - Chinese language version',
      'PL': 'Polish - Polish language version'
    };
    return tooltips[language] || `${language} - Card printed in ${language} language`;
  };

  const showTooltip = useCallback((content, event) => {
    if (!showTooltips) return; // Don't show tooltips if disabled

    const rect = event.target.getBoundingClientRect();
    const tooltipWidth = 280; // Max width of tooltip
    const tooltipHeight = 60; // Estimated height

    // Calculate horizontal position, ensuring it doesn't go off screen
    let x = rect.left + rect.width / 2;
    if (x - tooltipWidth / 2 < 10) {
      x = tooltipWidth / 2 + 10; // Left edge padding
    } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
      x = window.innerWidth - tooltipWidth / 2 - 10; // Right edge padding
    }

    // Calculate vertical position, ensuring it doesn't go off screen
    let y = rect.top - 15;
    if (y - tooltipHeight < 10) {
      y = rect.bottom + 15; // Show below if not enough space above
    }

    setTooltip({
      show: true,
      content,
      x,
      y
    });
  }, [showTooltips]);

  const hideTooltip = useCallback(() => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'd' || e.key === 'D') {
        isDKeyPressed.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'd' || e.key === 'D') {
        isDKeyPressed.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const isExcluded = useCallback((card) => {
    // Check if productId exists before calling toString()
    if (!card.productId) {
      return false;
    }
    
    return excludedSku.includes(card.productId.toString());
  }, [excludedSku]);

  const formatPrice = (price) => {
    return isNaN(price) ? '0.00' : price.toFixed(2);
  };



  const toggleBulkItems = () => {
    const newValue = !showBulkItems;
    setShowBulkItems(newValue);
    localStorage.setItem('showBulkItems', JSON.stringify(newValue));
    
    // Notify parent component about the change
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('bulkItemsToggle', { detail: { showBulkItems: newValue } }));
    }
  };

  const toggleTableView = () => {
    const newValue = !isTableView;
    setIsTableView(newValue);
    localStorage.setItem('isTableView', JSON.stringify(newValue));
  };

  const toggleNotBuying = () => {
    const newValue = !showNotBuying;
    setShowNotBuying(newValue);
    localStorage.setItem('showNotBuying', JSON.stringify(newValue));
    // Re-fetch so the server re-evaluates the showNotBuying mode. The backend now
    // excludes excludedSku cards from the count + page when hiding not-buying, so a
    // refetch (not just a client render filter) is needed to get the right page set.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('notBuyingToggle', { detail: { showNotBuying: newValue } }));
    }
  };

  const toggleTooltips = () => {
    const newValue = !showTooltips;
    setShowTooltips(newValue);
    localStorage.setItem('showTooltips', JSON.stringify(newValue));

    // Hide any currently visible tooltip when disabling
    if (!newValue) {
      hideTooltip();
    }
  };

  const handleItemsPerPageChange = (newValue) => {
    const value = parseInt(newValue);
    setItemsPerPage(value);
    localStorage.setItem('itemsPerPage', value.toString());

    // Notify parent to reset to page 1 and re-fetch
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('itemsPerPageChange', {
        detail: { itemsPerPage: value }
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <p>{t('Just fetching you the best prices...')}</p>
      </div>
    );
  }

  // Helper function to check if a card has fixed prices
  const getCardFixedPriceInfo = (card) => {
    if (!card.productId || !hotlistFixedPrices) return { hasFixedCash: false, hasFixedCredit: false };
    const productIdStr = String(card.productId);
    const fixedPriceData = hotlistFixedPrices[productIdStr];
    if (!fixedPriceData) return { hasFixedCash: false, hasFixedCredit: false };
    return {
      hasFixedCash: fixedPriceData.cashPrice !== undefined && fixedPriceData.cashPrice !== null,
      hasFixedCredit: fixedPriceData.creditPrice !== undefined && fixedPriceData.creditPrice !== null
    };
  };

  // The Simplistic shell uses the same resolved quote and SKU/add path as every other template.
  const renderSimplisticAction = (card, quote, quotesVisible = false) => {
    const isCash = paymentType === 'cash';
    const unavailable = isExcluded(card) || quote.isRecentRelease || quote.catalogIdentityError;
    const offerAvailable = !unavailable && !quote.contactForPrice && !quote.isBulk &&
      (isCash ? quote.cashPrice > 0 && quote.cashAvailable !== false : quote.creditPrice > 0 && quote.creditAvailable !== false);
    return <>
      {quotesVisible && <div className="simple-card-quotes"><span>Cash: {currencySymbols[userSettings.currency] || '$'}{formatPrice(quote.cashPrice || 0)}</span><span>Credit: {currencySymbols[userSettings.currency] || '$'}{formatPrice(quote.creditPrice || 0)}</span></div>}
      {unavailable ? <button type="button" className="simple-unavailable" disabled>Not currently buying</button> :
        quote.contactForPrice ? <button type="button" className="simple-unavailable" onClick={() => handleContactForPrice(card)}>Contact for price</button> :
        quote.isBulk ? <button type="button" className="simple-unavailable" onClick={() => setShowPopup(true)}>Bulk item · View details</button> :
        !offerAvailable ? <button type="button" className="simple-unavailable" disabled>Not buying {isCash ? 'for cash' : 'for credit'}</button> :
        <div className="simple-add-controls">
          {quotesVisible && <input type="number" min="1" max="4" aria-label={`Quantity for ${card.name}`} value={selectedQuantities[card._id] || 1} onChange={event => handleQuantityChange(card._id, event.target.value)} />}
          <button type="button" className="simple-primary" disabled={isCash && isCashLimitReached} onClick={() => handleAddToCart(card, isCash)}>{isCash && isCashLimitReached ? 'Cash limit reached' : orbit ? 'Add to cart' : 'Add to buylist'}</button>
        </div>}
    </>;
  };

  const renderOrbitCard = card => {
    const selection = getOrbitSelection(card, allowedConditions, { condition: selectedConditions[card._id], printing: selectedPrintings[card._id], language: selectedLanguages[card._id] }, userSettings);
    const quote = prices[card._id]?.currentPrice || { cashPrice: 0, creditPrice: 0, contactForPrice: true };
    const selectedPrice = paymentType === 'cash' ? quote.cashPrice : quote.creditPrice;
    return <article className="orbit-product" key={card._id}>
      <button type="button" className="orbit-product-image" aria-label={`Enlarge ${card.name}`} onClick={() => handleImageClick(card.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png', card.name)}>
        {selection.printing && selection.printing !== 'Normal' && <span className="orbit-printing-badge">{selection.printing}</span>}
        <img src={card.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'} alt={card.name} loading="lazy" onError={event => { event.currentTarget.onerror = null; event.currentTarget.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'; }} />
      </button>
      <h3 title={card.name}>{card.name}</h3>
      <p className="orbit-product-set" title={card.expansionName}>{card.expansionName || 'Set unavailable'}{card.number ? ` · #${card.number}` : ''}</p>
      {hotlistItems.includes(String(card.productId)) && <span className="orbit-hotlist-badge">Wanted card</span>}
      <label className="orbit-variant"><span className="orbit-sr-only">Condition and finish for {card.name}</span><select aria-label={`Condition and finish for ${card.name}`} value={JSON.stringify([selection.condition, selection.printing])} disabled={selection.sealed} onChange={event => { const [condition, printing] = JSON.parse(event.target.value); setSelectedConditions(previous => ({ ...previous, [card._id]: condition })); setSelectedPrintings(previous => ({ ...previous, [card._id]: printing })); }}>
        {selection.options.map(sku => <option key={JSON.stringify([sku.condAbbr, sku.printingName])} value={JSON.stringify([sku.condAbbr, sku.printingName])}>{orbitVariantLabel(sku)}</option>)}
      </select></label>
      {selection.languages.length > 1 && <label className="orbit-language"><span>Language</span><select aria-label={`Language for ${card.name}`} value={selection.language} onChange={event => handleLanguageChange(card._id, event.target.value)}>{selection.languages.map(language => <option key={language}>{language}</option>)}</select></label>}
      <div className="orbit-buy-price"><span>{paymentType === 'cash' ? 'Cash' : 'Credit'} offer</span><strong>{currencySymbols[userSettings.currency] || '$'}{formatPrice(selectedPrice || 0)}</strong></div>
      <div className="orbit-product-quantity"><button type="button" aria-label={`Decrease quantity for ${card.name}`} disabled={(selectedQuantities[card._id] || 1) <= 1} onClick={() => handleQuantityChange(card._id, (selectedQuantities[card._id] || 1) - 1)}>−</button><input aria-label={`Quantity for ${card.name}`} type="number" min="1" max="4" value={selectedQuantities[card._id] || 1} onChange={event => handleQuantityChange(card._id, event.target.value)} /><button type="button" aria-label={`Increase quantity for ${card.name}`} disabled={(selectedQuantities[card._id] || 1) >= 4} onClick={() => handleQuantityChange(card._id, (selectedQuantities[card._id] || 1) + 1)}>+</button></div>
      {renderSimplisticAction(card, quote)}
      {userSettings.socialMedia !== false && <ShareButtons cardName={card.name} price={Number(selectedPrice) || 0} currencySymbol={currencySymbols[userSettings.currency] || '$'} username={userSettings.username} card={card} />}
    </article>;
  };

  // No longer filtering cards here as it's done at the parent component level
  const renderCardView = (card) => {
    const cardConditions = selectableConditions(card, allowedConditions);
    const uniquePrintings = [...new Set(selectableSkus(card).map(sku => sku.printingName))];
    const condition = selectedConditions[card._id] || getDefaultCondition(cardConditions, allowedConditions, defaultCondition);
    const printing = selectedPrintings[card._id] || uniquePrintings[0] || '';
    const cardPriceData = prices[card._id] || { currentPrice: { cashPrice: 0, creditPrice: 0, isBulk: false, timestamp: null, contactForPrice: false } };
    const { cashPrice, creditPrice, isBulk, timestamp, contactForPrice, isDerivedPrice, derivedPercentage } = cardPriceData.currentPrice;
    const excluded = isExcluded(card); // Updated to only pass card
    const noPriceAvailable = cashPrice === 0 && creditPrice === 0 && !isBulk;
    const fixedPriceInfo = getCardFixedPriceInfo(card);

    return (
      <div key={card._id} className="card" onClick={() => handleOpenPriceDebug(card)}>
        <img
          src={card.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'}
          alt={card.name}
          className="card-image"
          onClick={(e) => { if (!isDKeyPressed.current) handleImageClick(card.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png', card.name); else e.stopPropagation(); }}
          onError={(e) => {
            console.log('Image failed to load:', card.image);
            e.target.onerror = null; // Prevent infinite loop
            e.target.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png';
          }}
        />
        <div className="card-info">
          <h3 className={`card-name ${card.productId && hotlistItems.includes(card.productId.toString()) ? 'hotlist-title' : ''}`} style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }}>{card.name}</h3>
          {card.productId && hotlistItems.includes(card.productId.toString()) && (
            <div className="hotlist-indicator">
              hotlist item
              {(fixedPriceInfo.hasFixedCash || fixedPriceInfo.hasFixedCredit) && (
                <span className="fixed-price-indicator" title={t('Fixed price set')}>ðŸ“Œ</span>
              )}
            </div>
          )}
          <p className="card-expansion">
            {card.expansionName || t('Expansion not available')}
          </p>
          {card.number && <p className="card-number">#{card.number}</p>}
          {/* Hidden productId for backend use - not displayed visually */}
          {!card.number && card.productId && <span className="card-product-id" data-product-id={card.productId} style={{ display: 'none' }}></span>}
          {card.rarity && !card.rarity.toLowerCase().trim().includes('sealed') && <p className="card-rarity">{card.rarity}</p>}
          {/* Hide dropdowns for sealed products */}
          {(!card.rarity || !card.rarity.toLowerCase().trim().includes('sealed')) && (
          <div className="card-options">
            <select
              className="condition-select"
              value={condition}
              onChange={(e) => handleConditionChange(card._id, e.target.value)}
              onMouseEnter={(e) => showTooltip(getConditionTooltip(condition), e)}
              onMouseLeave={hideTooltip}
            >
              {cardConditions.map((cond, index) => (
                <option key={index} value={cond}>{cond}</option>
              ))}
            </select>
            <select
              className="printing-select"
              value={printing}
              onChange={(e) => handlePrintingChange(card._id, e.target.value)}
              onMouseEnter={(e) => showTooltip(getPrintingTooltip(printing), e)}
              onMouseLeave={hideTooltip}
            >
              {uniquePrintings.map((printingOption, index) => (
                <option key={index} value={printingOption}>{printingOption}</option>
              ))}
            </select>
            {/* Language dropdown */}
            {(() => {
              // Use the availableLanguages from the price data if available, otherwise fall back to the card's SKUs
              const availableLanguages = prices[card._id]?.availableLanguages ||
                [...new Set(selectableSkus(card).map(sku => sku.langAbbr))];

              const language = selectedLanguages[card._id] ||
                (availableLanguages.includes(getNativeLanguage(card.gameName)) ? getNativeLanguage(card.gameName) : availableLanguages[0] || '');

              // Only show language dropdown if there are multiple languages
              if (availableLanguages.length > 1) {
                return (
                  <select
                    className="language-select"
                    value={language}
                    onChange={(e) => handleLanguageChange(card._id, e.target.value)}
                    onMouseEnter={(e) => showTooltip(getLanguageTooltip(language), e)}
                    onMouseLeave={hideTooltip}
                  >
                    {availableLanguages.map((lang, index) => (
                      <option key={index} value={lang}>{lang}</option>
                    ))}
                  </select>
                );
              } else if (availableLanguages.length === 1 && availableLanguages[0] === 'JP') {
                // If only Japanese is available, show a label
                return <span className="japanese-only-label">{t('Japanese Only')}</span>;
              }
              return null;
            })()}
          </div>
          )}
          <div className="card-actions">
            {simplistic ? renderSimplisticAction(card, cardPriceData.currentPrice, true) : contactForPrice ? (
              <button 
                className="add-to-cart-button contact-for-price amber-bg"
                onClick={() => handleContactForPrice(card)}
              >
                {t('Contact for Price')}
              </button>
            ) : cardPriceData.currentPrice.isRecentRelease || excluded || noPriceAvailable ? (
              <button className="add-to-cart-button not-buying" disabled>
                {t('Not Buying')}
              </button>
            ) : isBulk ? (
              <button className="add-to-cart-button bulk" onClick={() => setShowPopup(true)}>
                {t('Bulk Item')}
              </button>
            ) : (
              <>
                {creditPrice > 0 && (
                  <button 
                    className="add-to-cart-button credit"
                    onClick={() => handleAddToCart(card, false)}
                  >
                    {t('Credit')} {currencySymbols[userSettings.currency] || '$'}{formatPrice(creditPrice)}
                    {cardPriceData.currentPrice.isDerivedPrice && (
                      <span className="derived-price-indicator" title={`${t('Price calculated as')} ${cardPriceData.currentPrice.derivedPercentage}% ${t('of EN price')}`}>*</span>
                    )}
                  </button>
                )}
                {cashPrice > 0 ? (
                  <button 
                    className={`add-to-cart-button cash ${isCashLimitReached ? 'disabled' : ''}`}
                    onClick={() => handleAddToCart(card, true)}
                    disabled={isCashLimitReached}
                    title={isCashLimitReached ? t('Cash limit reached') : ''}
                  >
                    {isCashLimitReached ? t('Cash Limit Reached') : `${t('Cash')} ${currencySymbols[userSettings.currency] || '$'}${formatPrice(cashPrice)}`}
                    {!isCashLimitReached && cardPriceData.currentPrice.isDerivedPrice && (
                      <span className="derived-price-indicator" title={`${t('Price calculated as')} ${cardPriceData.currentPrice.derivedPercentage}% ${t('of EN price')}`}>*</span>
                    )}
                  </button>
                ) : (
                  <button className="add-to-cart-button not-buying" disabled>
                    {t('Not Buying Cash')}
                  </button>
                )}
              </>
            )}

            {/* Social Share Buttons */}
            {!simplistic && (creditPrice > 0 || cashPrice > 0) && userSettings.socialMedia !== false && (
              <ShareButtons
                cardName={card.name}
                price={Math.max(cashPrice || 0, creditPrice || 0)}
                currencySymbol={currencySymbols[userSettings.currency] || '$'}
                username={userSettings.username}
                card={card}
              />
            )}
          </div>

        </div>
      </div>
    );
  };

  const renderTableView = (filteredCards) => {
    return (
      <table className="card-table">
        <thead>
          <tr>
            <th>{t('Image')}</th>
            <th>{t('Name')}</th>
            <th>{t('Expansion')}</th>
            <th>{t('Number')}</th>
            <th>{t('Rarity')}</th>
            <th>{t('Condition')}</th>
            <th>{t('Printing')}</th>
            <th>{t('Language')}</th>
            <th>{t('Quantity')}</th>
            <th>{t('Credit Price')}</th>
            <th>{t('Cash Price')}</th>
            <th>{t('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredCards.map((card) => {
            const cardConditions = selectableConditions(card, allowedConditions);
            const uniquePrintings = [...new Set(selectableSkus(card).map(sku => sku.printingName))];
            const condition = selectedConditions[card._id] || getDefaultCondition(cardConditions, allowedConditions, defaultCondition);
            const printing = selectedPrintings[card._id] || uniquePrintings[0] || '';
            const cardPriceData = prices[card._id] || { currentPrice: { cashPrice: 0, creditPrice: 0, isBulk: false, timestamp: null, contactForPrice: false } };
            const { cashPrice, creditPrice, isBulk, contactForPrice, isDerivedPrice, derivedPercentage } = cardPriceData.currentPrice;
            const excluded = isExcluded(card); // Updated to only pass card
            const noPriceAvailable = cashPrice === 0 && creditPrice === 0 && !isBulk;
            const fixedPriceInfo = getCardFixedPriceInfo(card);

            return (
              <tr key={card._id} onClick={() => handleOpenPriceDebug(card)} style={{ cursor: isDKeyPressed.current ? 'help' : 'default' }}>
                <td>
                  {card.image ? (
                    <img
                      src={card.image}
                      alt={card.name}
                      className="table-card-image"
                      onClick={(e) => { if (!isDKeyPressed.current) handleImageClick(card.image, card.name); else e.stopPropagation(); }}
                      onError={(e) => {
                        console.log('Table image failed to load:', card.image);
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png';
                      }}
                    />
                  ) : (
                    <img
                      src={process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'}
                      alt={card.name}
                      className="table-card-image"
                      onClick={(e) => { if (!isDKeyPressed.current) handleImageClick(process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png', card.name); else e.stopPropagation(); }}
                    />
                  )}
                </td>
                <td>
                  <span className={card.productId && hotlistItems.includes(card.productId.toString()) ? 'hotlist-title' : ''}>{card.name}</span>
                  {card.productId && hotlistItems.includes(card.productId.toString()) && (
                    <div className="hotlist-indicator">
                      hotlist item
                      {(fixedPriceInfo.hasFixedCash || fixedPriceInfo.hasFixedCredit) && (
                        <span className="fixed-price-indicator" title={t('Fixed price set')}>ðŸ“Œ</span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  {card.expansionName || t('Expansion not available')}
                </td>
                <td>
                  {card.number ? `#${card.number}` : t('N/A')}
                </td>
                <td>
                  {card.rarity?.toLowerCase().trim().includes('sealed') ? t('N/A') : (card.rarity || t('N/A'))}
                </td>
                <td>
                  {card.rarity?.toLowerCase().trim().includes('sealed') ? t('N/A') : (
                  <select
                    value={condition}
                    onChange={(e) => handleConditionChange(card._id, e.target.value)}
                    onMouseEnter={(e) => showTooltip(getConditionTooltip(condition), e)}
                    onMouseLeave={hideTooltip}
                  >
                    {cardConditions.map((cond, index) => (
                      <option key={index} value={cond}>{cond}</option>
                    ))}
                  </select>
                  )}
                </td>
                <td>
                  {card.rarity?.toLowerCase().trim().includes('sealed') ? t('N/A') : (
                  <select
                    value={printing}
                    onChange={(e) => handlePrintingChange(card._id, e.target.value)}
                    onMouseEnter={(e) => showTooltip(getPrintingTooltip(printing), e)}
                    onMouseLeave={hideTooltip}
                  >
                    {uniquePrintings.map((printingOption, index) => (
                      <option key={index} value={printingOption}>{printingOption}</option>
                    ))}
                  </select>
                  )}
                </td>
                <td>
                  {card.rarity?.toLowerCase().trim().includes('sealed') ? t('N/A') : (
                  (() => {
                    // Use the availableLanguages from the price data if available, otherwise fall back to the card's SKUs
                    const availableLanguages = prices[card._id]?.availableLanguages ||
                      [...new Set(selectableSkus(card).map(sku => sku.langAbbr))];

                    const language = selectedLanguages[card._id] ||
                      (availableLanguages.includes(getNativeLanguage(card.gameName)) ? getNativeLanguage(card.gameName) : availableLanguages[0] || '');

                    // Only show language dropdown if there are multiple languages
                    if (availableLanguages.length > 1) {
                      return (
                        <select
                          value={language}
                          onChange={(e) => handleLanguageChange(card._id, e.target.value)}
                          onMouseEnter={(e) => showTooltip(getLanguageTooltip(language), e)}
                          onMouseLeave={hideTooltip}
                        >
                          {availableLanguages.map((lang, index) => (
                            <option key={index} value={lang}>{lang}</option>
                          ))}
                        </select>
                      );
                    } else if (availableLanguages.length === 1 && availableLanguages[0] === 'JP') {
                      // If only Japanese is available, show a label
                      return <span className="japanese-only-label">{t('Japanese Only')}</span>;
                    } else {
                      return language;
                    }
                  })()
                  )}
                </td>
                <td>
                  <input
                    type="number"
                    min="1"
                    max={simplistic ? 4 : 999}
                    value={selectedQuantities[card._id] || 1}
                    onChange={(e) => handleQuantityChange(card._id, e.target.value)}
                    className="quantity-input"
                  />
                </td>
                <td>
                  {currencySymbols[userSettings.currency] || '$'}{formatPrice(creditPrice)}
                  {cardPriceData.currentPrice.isDerivedPrice && (
                    <span className="derived-price-indicator" title={`${t('Price calculated as')} ${cardPriceData.currentPrice.derivedPercentage}% ${t('of EN price')}`}>*</span>
                  )}
                </td>
                <td>
                  {currencySymbols[userSettings.currency] || '$'}{formatPrice(cashPrice)}
                  {cardPriceData.currentPrice.isDerivedPrice && (
                    <span className="derived-price-indicator" title={`${t('Price calculated as')} ${cardPriceData.currentPrice.derivedPercentage}% ${t('of EN price')}`}>*</span>
                  )}
                </td>
                <td>
                  {simplistic ? renderSimplisticAction(card, cardPriceData.currentPrice) : contactForPrice ? (
                    <button 
                      className="add-to-cart-button contact-for-price amber-bg"
                      onClick={() => handleContactForPrice(card)}
                    >
                      {t('Contact for Price')}
                    </button>
                  ) : cardPriceData.currentPrice.isRecentRelease || excluded || noPriceAvailable ? (
                    <button className="add-to-cart-button not-buying" disabled>
                      {t('Not Buying')}
                    </button>
                  ) : isBulk ? (
                    <button className="add-to-cart-button bulk" onClick={() => setShowPopup(true)}>
                      {t('Bulk Item')}
                    </button>
                  ) : (
                    <>
                      <button
                        className="add-to-cart-button credit"
                        onClick={() => handleAddToCart(card, false)}
                      >
                        {t('Add Credit')}
                      </button>
                      <button
                        className={`add-to-cart-button cash ${isCashLimitReached ? 'disabled' : ''}`}
                        onClick={() => handleAddToCart(card, true)}
                        disabled={isCashLimitReached}
                        title={isCashLimitReached ? t('Cash limit reached') : ''}
                      >
                        {isCashLimitReached ? t('Cash Limit') : t('Add Cash')}
                      </button>
                      {/* Social Share Buttons */}
                      {userSettings.socialMedia !== false && (
                        <ShareButtons
                          cardName={card.name}
                          price={Math.max(cashPrice || 0, creditPrice || 0)}
                          currencySymbol={currencySymbols[userSettings.currency] || '$'}
                          username={userSettings.username}
                          card={card}
                        />
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="card-list-container">
      {/* Hide entire toggle row for SmokeandMirrors */}
      {!isSmokeandMirrors && !simplistic && (
        <div className="toggle-row">
          {/* Search Mode Toggle - only show if enabled */}
          {showSearchModeToggle && (
            <div className="search-mode-toggle-container">
              <button
                className="search-mode-toggle-button"
                onClick={onSearchModeToggle}
              >
                {currentSearchMode === "single" ? t('Bulk Search') : t('Single Search')}
              </button>
            </div>
          )}

          {/* Bulk toggle removed as requested, but functionality kept in code */}
          <div className="view-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={isTableView}
                onChange={toggleTableView}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-label">{t('Table View')}</span>
          </div>
          <div className="view-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={showNotBuying}
                onChange={toggleNotBuying}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-label">{t('Show Not Buying')}</span>
          </div>
          <div className="view-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={showTooltips}
                onChange={toggleTooltips}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-label">{t('Show Tooltips')}</span>
          </div>
          <div className="results-per-page-selector">
            <span className="toggle-label">{t('Results per page')}:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              className="results-per-page-dropdown"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="75">75</option>
              <option value="100">100</option>
            </select>
          </div>
          {/* Language selector - commented out as it's not being used
          <div className="language-selector">
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="language-dropdown"
            >
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="pl">Polski</option>
            </select>
          </div>
          */}
        </div>
      )}
      {/* Show only Not Buying toggle for MinMaxGames in v3 template */}
      {isMinMaxGames && (
        <div className="toggle-row">
          <div className="view-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={showNotBuying}
                onChange={toggleNotBuying}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-label">{t('Show Not Buying')}</span>
          </div>
        </div>
      )}
      <div className={`card-list ${isTableView ? 'table-view' : ''}`}>
        {(() => {
          // Filter cards - exclude "code card" items and apply showNotBuying filter
          const filteredCards = cards.filter(card => {
            // Always exclude "code card" items (case-insensitive)
            if (card.name && card.name.toLowerCase().includes('code card')) return false;

            if (showNotBuying) return true;

            // If prices haven't loaded for this card yet, show it (don't filter it out)
            if (!prices[card._id]) return true;

            const cardPriceData = prices[card._id];
            const { cashPrice, creditPrice, isBulk, isRecentRelease } = cardPriceData.currentPrice || {};
            const excluded = isExcluded(card);
            const noPriceAvailable = cashPrice === 0 && creditPrice === 0 && !isBulk;

            // If any of these conditions are true, the card is "not buying"
            // Note: isBulk items ARE buyable, so we don't filter them out
            const isNotBuying = isRecentRelease || excluded || noPriceAvailable;

            // Only show the card if it's not a "not buying" card
            return !isNotBuying;
          });

          // Show "no results" message if no cards match filters
          if (filteredCards.length === 0) {
            return (
              <div className="no-results-message">
                <p>{t('We are not currently buying any of the matching results. Please check back regularly as our buylist is updated frequently.')}</p>
              </div>
            );
          }

          if (isTableView) {
            return renderTableView(filteredCards);
          }

          // Keep card view inside the existing responsive CSS grid at every result
          // count. A one-dimensional virtual list becomes a single grid item and
          // collapses all cards into the first column.
          return filteredCards.map(orbit ? renderOrbitCard : renderCardView);
        })()}
      </div>
      {showPopup && <Popup content={bulkSettings} onClose={closePopup} simplistic={simplistic} />}
      {showSkuDetailsPopup && selectedCard && (
        <SkuDetailsPopup
          card={selectedCard}
          sku={sealedSelection(selectedCard).sealed ? sealedSelection(selectedCard).sku : selectableSkus(selectedCard).find(sku =>
            sku.condAbbr === (selectedConditions[selectedCard._id] || getDefaultCondition(selectableConditions(selectedCard, allowedConditions), allowedConditions, defaultCondition)) &&
            sku.printingName === (selectedPrintings[selectedCard._id] || selectableSkus(selectedCard)[0]?.printingName) &&
            sku.langAbbr === (selectedLanguages[selectedCard._id] || getNativeLanguage(selectedCard.gameName))
          )}
          onClose={closeSkuDetailsPopup}
          calculatePrice={calculatePrice}
          userSettings={userSettings}
          exchangeRates={exchangeRates}
        />
      )}
      {showPriceDebugPopup && selectedCard && (
        <PriceDebugPopup
          card={selectedCard}
          onClose={closePriceDebugPopup}
          calculatePrice={calculatePrice}
          userSettings={userSettings}
        />
      )}
      {showPriceInquiryForm && selectedCard && (() => {
        const cardConditions = selectableConditions(selectedCard, allowedConditions);
        return (
          <PriceInquiryForm
            card={selectedCard}
            condition={selectedConditions[selectedCard._id] || getDefaultCondition(cardConditions, allowedConditions, defaultCondition)}
            printing={selectedPrintings[selectedCard._id] || selectableSkus(selectedCard)[0]?.printingName || ''}
            onClose={closePriceInquiryForm}
            userSettings={userSettings}
          />
        );
      })()}
      {enlargedImage && (
        <div className="image-modal-overlay" onClick={closeEnlargedImage}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeEnlargedImage}>
              ×
            </button>
            <img
              src={enlargedImage.url}
              alt={enlargedImage.name}
              className="enlarged-image"
            />
            <div className="image-modal-title">{enlargedImage.name}</div>
          </div>
        </div>
      )}
      {tooltip.show && (
        <div
          className="custom-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: tooltip.y > 100 ? 'translate(-50%, -100%)' : 'translate(-50%, 0%)',
            zIndex: 1002
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default React.memo(CardList);
